import type { GetStaticPaths, GetStaticProps } from 'next'
import type { BlogPost } from '@/types'

import { formatDate } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { FadeIn } from '@/components/animation/reveal'
import { MarqueeBand } from '@/components/layout/marquee-band'
import { BlogGrid } from '@/components/blog/blog-grid'
import {
    getBlogPosts,
    getBlogPostBySlug,
    getBlogSlugs
} from '@/lib/content'

interface PostDetailProps {
    post: BlogPost
    others: BlogPost[]
}

export default function PostDetail({ post, others }: PostDetailProps) {
    return (
        <>
            <Seo
                title={post.name}
                description={post.summary}
                ogImage={post.main}
            />

            <div className='section'>
                <div className='container main-hero-container'>
                    <div className='perspective'>
                        <FadeIn className='fade-in-2-slow-on-view'>
                            <h1 className='post-details-title'>{post.name}</h1>
                        </FadeIn>
                    </div>

                    <img
                        loading='lazy'
                        src={post.main}
                        alt={post.name}
                        className='post-detail-image'
                    />

                    <div className='w-layout-grid grid-6-columns'>
                        <div
                            id='w-node-ea428a66-ffeb-86ca-a262-352421be21f8-780953d3'
                            className='post-item-content-bottom'
                        >
                            <p>{post.category}</p>
                            <div>{formatDate(post.publishedOn)}</div>
                        </div>
                        <div
                            id='w-node-_649d3aac-aab8-de00-e79e-280d29ee3758-780953d3'
                            className='content'
                        >
                            <FadeIn className='fade-in-slow-on-view'>
                                <div>{post.summary}</div>
                            </FadeIn>
                        </div>
                    </div>

                    <div className='horizontal-line-container'>
                        <FadeIn className='horizontal-line' />
                    </div>

                    <FadeIn>
                        <div
                            className='rich-text w-richtext'
                            dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
                        />
                    </FadeIn>
                </div>
            </div>

            <MarqueeBand words={['BLOG POSTS', 'FROM', 'THE PAST.']} />

            {others.length > 0 ? (
                <div className='section'>
                    <div className='container'>
                        <BlogGrid posts={others} />
                    </div>
                </div>
            ) : null}
        </>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getBlogSlugs()
    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<PostDetailProps> = async ({
    params
}) => {
    const slug = params?.slug as string
    const post = await getBlogPostBySlug(slug)
    if (!post) return { notFound: true }

    const all = await getBlogPosts()
    const others = all.filter((item) => item.slug !== slug).slice(0, 3)

    return { props: { post, others }, revalidate: 60 }
}
